import debug from '../utils/console'
import { getTenantGroupKey } from '../openfeature/cacheKey'
import type { Client, JsonValue } from '@openfeature/web-sdk'
import type { ServerFlagState } from '../openfeature/serverFlagClient'

const logger = debug.withTag('ssr-flag-check')

/** One flag, as the server resolved it and as the browser resolves it. */
export interface FlagComparison {
  flag: string
  server: JsonValue
  browser: JsonValue
  agree: boolean
}

/**
 * The browser has to be asked for the same type the server resolved, or a
 * boolean flag comes back as the string default and every one of them looks
 * like a disagreement.
 */
function readAs(client: Client, flag: string, server: JsonValue): JsonValue {
  if (typeof server === 'boolean') return client.getBooleanValue(flag, server)
  if (typeof server === 'number') return client.getNumberValue(flag, server)
  if (typeof server === 'string') return client.getStringValue(flag, server)
  return null
}

/** Compares every flag the render reads. The beacon and the table share it. */
export function compareFlags(
  client: Client,
  state: ServerFlagState,
  flags: string[]
): FlagComparison[] {
  return flags.flatMap((flag) => {
    const entry = state.values[flag]
    if (!entry) return []
    const server = entry.value
    const browser = readAs(client, flag, server)
    return [{ flag, server, browser, agree: server === browser }]
  })
}

/**
 * The canary on the group cache. A rendered page was produced from the group's
 * attributes alone, so every flag that shapes it has to evaluate the same way
 * in the browser, which still has the visitor's full context. A disagreement
 * means the flag reads something the group key does not carry, and the page
 * cached for that group is wrong for part of it.
 *
 * It reports rather than repairs, and it asks the browser provider directly.
 * The page client keeps answering render flags from the server for the whole
 * view, so the visitor sees one consistent page either way, and the report is
 * what says the flag has to move or the group key has to grow. A change to a
 * render flag also shows up here for up to an hour, while the proxied config
 * browsers read catches up with the server, and stops on its own.
 *
 * On a review build it also prints the whole comparison, so a tester can see
 * which flag disagreed without reading the Datadog logs. The table is compiled
 * out of a production build.
 */
export default defineNuxtPlugin({
  name: 'ssr-flag-check',
  setup(nuxtApp) {
    if (!nuxtApp.payload.serverRendered) return
    const state = useState<ServerFlagState | null>('ssr-flags').value
    if (!state || state.degraded) return
    const group = String(state.context.targetingKey ?? '')
    // Every flag the home render reads, not only the ones behind a config
    // store. A directly read flag such as shiftNewsSearchBoxVariant is exactly
    // the kind that can differ without anything else noticing.
    const watched = getTenantGroupKey(useAppConfig().tenant ?? '')?.renderFlags
    if (!watched?.length) return
    // Read here, not in the callback below. The Nuxt instance is gone after the
    // first await, and useRuntimeConfig throws once it is, which would take the
    // beacon with it.
    const debugBuild = Boolean(useRuntimeConfig().public.ssrGateDebug)

    // After the app is idle: the check costs nothing the visitor waits for.
    onNuxtReady(async () => {
      const { client, browser } = await useOpenFeature()
      const compared = compareFlags(browser ?? client, state, watched)

      if (debugBuild) {
        // This plugin only runs on a page the server rendered, so the verdict
        // is settled. The x-ssr-gate response header is what names the other
        // verdicts, on the loads that got the shell instead.
        logger.log(`group ${group}, gate render`)
        window.__ssrFlags = { group, verdict: 'render', flags: compared }
        // Consola replaces console in the browser and has no table, so the
        // nice rendering is a bonus rather than something to depend on.
        if (typeof console.table === 'function') {
          console.table(compared)
        } else {
          logger.log(compared)
        }
      }

      for (const { flag, server, browser, agree } of compared) {
        if (agree) continue
        await $fetch('/api/ssr-flag-check', {
          method: 'POST',
          body: { group, flag, server, browser },
        }).catch((error: unknown) => {
          // A report that does not arrive must not take the page with it.
          logger.warn('Failed to report a flag disagreement', error)
        })
      }
    })
  },
})
